import { useState } from 'react'
import { CreatableTagInput, type TagItem } from '../ui/CreatableTagInput'
import type { AcademicDegree, Institution } from '../../types/talent'

const LEVEL_LABELS: Record<string, string> = {
  bachelors: "Bachelor's",
  masters: "Master's",
  phd: 'PhD',
  postdoc: 'Post-doc',
  doctor: 'Medical Doctor',
}

const ADDABLE_LEVELS = [
  { value: 'masters', label: "Master's" },
  { value: 'phd', label: 'PhD' },
  { value: 'postdoc', label: 'Post-doc' },
  { value: 'doctor', label: 'Medical Doctor' },
]

interface AcademicBackgroundProps {
  degrees: AcademicDegree[]
  onAdd: (level: string) => Promise<void>
  onUpdate: (id: string, data: { field_of_study?: string; institution_ids?: number[] }) => Promise<void>
  onDelete: (id: string) => Promise<void>
  onSearchInstitutions: (query: string) => Promise<Institution[]>
  onCreateInstitution: (name: string) => Promise<Institution>
}

export function AcademicBackground({
  degrees,
  onAdd,
  onUpdate,
  onDelete,
  onSearchInstitutions,
  onCreateInstitution,
}: AcademicBackgroundProps) {
  const [isAdding, setIsAdding] = useState(false)
  const [addingLevel, setAddingLevel] = useState<string | null>(null)
  const [fieldTimers, setFieldTimers] = useState<Record<string, ReturnType<typeof setTimeout>>>({})

  const existingLevels = new Set(degrees.map((d) => d.level))
  const hasBachelors = existingLevels.has('bachelors')
  const availableLevels = ADDABLE_LEVELS.filter((l) => !existingLevels.has(l.value as AcademicDegree['level']))

  const handleAddDegree = async (level: string) => {
    setAddingLevel(level)
    try {
      await onAdd(level)
    } finally {
      setAddingLevel(null)
      setIsAdding(false)
    }
  }

  const handleFieldChange = (degreeId: string, value: string, institutions: Institution[]) => {
    // Debounce field_of_study updates
    if (fieldTimers[degreeId]) clearTimeout(fieldTimers[degreeId])
    const timer = setTimeout(() => {
      onUpdate(degreeId, {
        field_of_study: value,
        institution_ids: institutions.map((i) => i.id),
      })
    }, 500)
    setFieldTimers((prev) => ({ ...prev, [degreeId]: timer }))
  }

  const handleInstitutionAdd = (degree: AcademicDegree, institution: Institution) => {
    const newIds = [...degree.institutions.map((i) => i.id), institution.id]
    onUpdate(degree.id, { institution_ids: newIds })
  }

  const handleInstitutionRemove = (degree: AcademicDegree, institution: TagItem) => {
    const newIds = degree.institutions
      .filter((i) => i.id !== institution.id)
      .map((i) => i.id)
    onUpdate(degree.id, { institution_ids: newIds })
  }

  const searchInstitutionsForTag = async (query: string): Promise<TagItem[]> => {
    const results = await onSearchInstitutions(query)
    return results.map((i) => ({ id: i.id, name: i.name }))
  }

  const createInstitutionForTag = async (name: string): Promise<TagItem> => {
    const inst = await onCreateInstitution(name)
    return { id: inst.id, name: inst.name }
  }

  return (
    <div className="space-y-4">
      {/* Show Bachelor's prompt if none exists */}
      {!hasBachelors && degrees.length === 0 && (
        <button
          type="button"
          onClick={() => handleAddDegree('bachelors')}
          disabled={addingLevel === 'bachelors'}
          className="w-full p-4 border-2 border-dashed border-secondary-300 rounded-lg text-secondary-500 hover:border-primary-400 hover:text-primary-600 transition-colors text-sm"
        >
          {addingLevel === 'bachelors' ? 'Adding...' : "+ Add your Bachelor's degree"}
        </button>
      )}

      {/* Existing degrees */}
      {degrees.map((degree) => (
        <DegreeEntry
          key={degree.id}
          degree={degree}
          onFieldChange={(value) =>
            handleFieldChange(degree.id, value, degree.institutions)
          }
          onInstitutionAdd={(inst) => handleInstitutionAdd(degree, inst as Institution)}
          onInstitutionRemove={(inst) => handleInstitutionRemove(degree, inst)}
          onDelete={() => onDelete(degree.id)}
          searchInstitutions={searchInstitutionsForTag}
          createInstitution={createInstitutionForTag}
        />
      ))}

      {/* Add degree button */}
      {availableLevels.length > 0 && (
        <div>
          {isAdding ? (
            <div className="flex gap-2 items-center">
              <span className="text-sm text-secondary-600">Add degree:</span>
              {availableLevels.map((level) => (
                <button
                  key={level.value}
                  type="button"
                  onClick={() => handleAddDegree(level.value)}
                  disabled={addingLevel !== null}
                  className="px-3 py-1 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors disabled:opacity-50"
                >
                  {addingLevel === level.value ? 'Adding...' : level.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setIsAdding(false)}
                className="text-sm text-secondary-400 hover:text-secondary-600"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => (hasBachelors || degrees.length > 0) ? setIsAdding(true) : handleAddDegree('bachelors')}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              + Add degree
            </button>
          )}
        </div>
      )}
    </div>
  )
}

interface DegreeEntryProps {
  degree: AcademicDegree
  onFieldChange: (value: string) => void
  onInstitutionAdd: (institution: TagItem) => void
  onInstitutionRemove: (institution: TagItem) => void
  onDelete: () => void
  searchInstitutions: (query: string) => Promise<TagItem[]>
  createInstitution: (name: string) => Promise<TagItem>
}

function DegreeEntry({
  degree,
  onFieldChange,
  onInstitutionAdd,
  onInstitutionRemove,
  onDelete,
  searchInstitutions,
  createInstitution,
}: DegreeEntryProps) {
  const [fieldValue, setFieldValue] = useState(degree.field_of_study)

  const handleFieldBlur = () => {
    if (fieldValue !== degree.field_of_study) {
      onFieldChange(fieldValue)
    }
  }

  return (
    <div className="p-4 border border-secondary-200 rounded-lg space-y-3">
      <div className="flex items-center justify-between">
        <span className="font-medium text-secondary-900">
          {LEVEL_LABELS[degree.level] || degree.level}
        </span>
        <button
          type="button"
          onClick={onDelete}
          className="text-secondary-400 hover:text-red-500 transition-colors"
          title="Remove degree"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div>
        <label className="text-sm text-secondary-600 block mb-1">Field of Study</label>
        <input
          type="text"
          className="input"
          placeholder="e.g., Computer Science, Public Health"
          value={fieldValue}
          onChange={(e) => setFieldValue(e.target.value)}
          onBlur={handleFieldBlur}
        />
      </div>
      <div>
        <label className="text-sm text-secondary-600 block mb-1">Institution(s)</label>
        <CreatableTagInput
          value={degree.institutions.map((i) => ({ id: i.id, name: i.name }))}
          onSearch={searchInstitutions}
          onCreate={createInstitution}
          onAdd={onInstitutionAdd}
          onRemove={onInstitutionRemove}
          placeholder="Search or add institution..."
          createLabel="Add"
        />
      </div>
    </div>
  )
}
